import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Card, CardBody, FormGroup, Label } from "reactstrap";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Select from "react-select";
import { showLoader } from "../../store/actions/mainActions";
import { PostService } from "../../globals/services/post.service";
import { useDispatch, useSelector } from "react-redux";
import { PostsList } from "../../globals/core/List/PostsList";
import makeAnimated from "react-select/animated";
import { EventsService } from "../../globals/services/events.service";
import msgIcon from "../../assets/images/msgIcon.svg";
import "./CurrentEventEditForm.scss"
import styles from "./CurrentEventEditForm.module.scss";
import EventChanges from "./components/EventForm/EventChanges";
import EventForm from "./components/EventForm";
import EventNavbar from "./components/EventNavbar";

const animatedComponents = makeAnimated();

export const CurrentEventEditForm = (props) => {
  const initialState = {
    _id: null,
    eventName: "",
    eventTitle: "",
    address: { city: "", state: "", street: "", country: "", zip: "" },
    dtStartDate: "",
    dtEndDate: "",
    language: "",
    attendance: "Always appears",
    description: "",
    time: "",
    thumbnail: "",
    link: "",
    related: [],
    relatedSessions: [],
    communityIds: [],
    eventUpdated: false,
    hasImage: false,
  };

  const eventId = props.match.params.id;
  const [updateNow, setUpdateNow] = useState(false);
  const [state, setState] = useState(initialState);
  const [itemChange, setItemChange] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { communities } = useSelector((state) => state.content);
  const { me } = useSelector((state) => state.auth);
  const [communitiesToBePublishedTo, setCommunitiesToBePublishedTo] = useState([]);
  const [posts, setPosts] = useState([]);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const _communities = communities.filter((c) => c.label.indexOf("- Community") !== -1);
  
  useEffect(() => {
    const getEventData = async () => {
      try {
        setIsLoading(true);
        const _event = await EventsService().getEventById(eventId);
        const eventStatedata = _event.data;   
        let event;

        if (!eventStatedata?.attendance || eventStatedata?.attendance === "") {
          const updatedEvent = { ...eventStatedata, attendance: "Always appears" };
          await EventsService().updateEvent(updatedEvent);
          const updatedEventData = await EventsService().getEventById(eventId);
          event = updatedEventData.data;
        } else {
          event = eventStatedata;
        }

        const initialState = {
          _id: event?._id,
          sfSeminarId: event?.sfSeminarId ? event.sfSeminarId : "",
          eventName: event?.eventName,
          eventTitle: event?.eventTitle ? event.eventTitle : "",
          address: event?.address ? event?.address : { city: "", state: "", street: "", country: "", zip: "" },
          dtStartDate: event?.dtStartDate ? event.dtStartDate : "",
          dtEndDate: event?.dtEndDate ? event.dtEndDate : "",
          language: event?.language ? event.language : "",
          attendance: event?.attendance ? event.attendance : "",
          description: event?.description ? event.description : "",
          time: event?.time ? event.time : "",
          thumbnail: event?.thumbnail ? event.thumbnail : "",
          hasImage: Boolean(event?.thumbnail),
          link: event?.link ? event.link : "",
          related: event?.related ? event.related : [],
          relatedSessions: event?.sessions ? event.sessions : [],
          participants: event?.users ? event.users : [],
          seminarTasks: event?.seminarTasks ? event.seminarTasks : [],
          msgBody: "Message body goes here",
          msgType: null,
          communityIds: setCommunities(event),
        };

        setState(initialState);
        await getRelatedPosts(_event.data?._id);
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching event data:", error);
        setIsLoading(false);
        // If it's a 401 error, the axios interceptor will handle it
        // For other errors, we can show a user-friendly message
      }
    };
    
    // Only fetch event data if we have user data (me) available
    if (me) {
      getEventData();
    } else {
      // If no user data, keep loading state
      setIsLoading(true);
    }
  }, [updateNow, location?.state?.updateEvent, me]);

  const forceUpdate = () => {
    setUpdateNow(!updateNow);
  };

  const publishEvent = async (event, publishTo, callback) => {
    try {
      await EventsService().postEventItem({ communityIds: publishTo, contentItemId: event, eventId: event });

      await callback(); //getting all posts

      history.push({
        pathname: "/Events",
      });
    } catch (err) {
      throw err;
    }
  };

  const getRelatedPosts = useCallback(
    async (eventId) => {
      try {
        dispatch(showLoader(true));
        const postService = PostService();
        const posts = await postService.getPostsByEventId(eventId);
        setPosts(posts);
        dispatch(showLoader(false));
      } catch (err) {
        dispatch(showLoader(false));
        console.error(err);
      }
    },
    [setPosts, dispatch, state.eventUpdated]
  );

  useEffect(() => {
    getCommunitiesToPublishTo();
  }, [posts]);

  const setCommunities = (event) => {
    return event?.communityIds && Array.isArray(event.communityIds) ? communities.filter((c) => event.communityIds.includes(c.value)) : [];
  };

  const getCommunitiesToPublishTo = () => {
    let res = [];
    const cIds = state.communityIds.map((co) => co.value);
    const postsCids = posts.map((p) => p.communityId);
    const cIdsToBePublishTo = cIds.filter((cId) => !postsCids.includes(cId));
    if (cIdsToBePublishTo.length > 0) {
      res = [...new Set(cIdsToBePublishTo)];
    }
    setCommunitiesToBePublishedTo(res);
  };

  const handleMultiSelect = (prop) => {
    //add
    if (prop.communityIds.length > state.communityIds.length) {
      setState({ ...state, ...prop });
      //remove
    } else {
      let _missing = "";
      let stateValues = state.communityIds.map((sc) => sc.value);
      let propValues = prop.communityIds.map((pc) => pc.value);
      stateValues.forEach((sv) => {
        if (!propValues.includes(sv)) _missing = sv;
      });
      if (communitiesToBePublishedTo.includes(_missing)) {
        setState({ ...state, ...prop });
      }
    }
  };

  const togglePostStatus = async (p) => {
    dispatch(showLoader(true));
    const status = p.status === "publish" ? "draft" : "publish";
    const postService = PostService();
    await postService.editPost({ status }, p._id);
    dispatch(showLoader(false));
    await getRelatedPosts();
  };

  const renderPostUnits = (posts) => {
    return <PostsList posts={posts} togglePost={togglePostStatus} />;
  };

  const goToNotification = () => {
    history.push({
      pathname: "/notification",
      state: { item: state },
    });
  };

  const handleGoBack = () => history.goBack();

  // Show loading indicator while user data is being fetched
  if (isLoading || !me) {
    return (
      <div className={`container ${styles.container}`}>
        <Card className={styles.card}>
          <CardBody>
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading event data...</p>
            </div>
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className={`container ${styles.container}`}>
      <Card className={styles.card}>
        <CardBody>
          <div className={styles.header}>
            <Button className={styles.backButton} onClick={handleGoBack}>
              <ArrowBackIcon />
            </Button>
            <div className={styles.headerText}>
              <h3 className={styles.title}>Edit Event</h3>
              <div className={styles.subtitle}>
                Here you can set up your event and its contents.
              </div>
            </div>
            <EventChanges
              state={state}
              itemWasChanged={itemChange}
            />
          </div>
          <EventForm
            formState={state}
            setFormState={setState}
            setItemChange={setItemChange}
          />
        </CardBody>
      </Card>

      <EventNavbar
        formState={state}
        forceUpdate={forceUpdate}
        setFormState={setState}
        setItemChange={setItemChange}
      />

      {/* COMMUNITY TARGETS */}
      <div className={styles.communities}>
        <FormGroup className="mb-3 mt-3 mt-lg-0 templating-select select2-container">
          <h5 className="mt-4 my-2">
            <Label htmlFor="communities">Pick a community to post to (1 or more) </Label>
          </h5>
          <h6 className="mt-4 my-2">
            <Label htmlFor="communities">Once Selected can be modified publish status with the table below </Label>
          </h6>
          <Select
            isClearable={false}
            id="communities"
            value={state.communityIds}
            isMulti={true}
            onChange={(selected) => handleMultiSelect({ communityIds: selected })}
            options={_communities}
            classNamePrefix="select2-selection"
            closeMenuOnSelect={false}
            components={animatedComponents}
          />
        </FormGroup>
      </div>
      <div className={styles.communities}>
        <div className="d-flex justify-content-end">
          <Button onClick={() => publishEvent(eventId, communitiesToBePublishedTo, getRelatedPosts)} color={"primary"}>
            Publish Event
          </Button>
        </div>
      </div>
      <div className={styles.communities}>{renderPostUnits(posts)}</div>

      <div className="msgDialogBtn">
        <button className="msgDialogBtn" onClick={goToNotification}>
          <img src={msgIcon} alt="Message Dialog Icon" />
        </button>
      </div>
    </div>
  );
};
